import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';

const OPTIONS = [
  { value: 'system', icon: Monitor },
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
];

export default function ThemeSwitcher({ compact = false, placement = 'bottom', className = '' }) {
  const { preference, resolvedTheme, setPreference } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const CurrentIcon = resolvedTheme === 'dark' ? Moon : Sun;
  const currentLabel = t(`theme.${preference}`);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`theme-switcher${compact ? ' theme-switcher-compact' : ''}${placement === 'top' ? ' theme-switcher-up' : ''} ${className}`.trim()}
    >
      <button
        type="button"
        className="theme-trigger"
        aria-label={t('theme.current', { theme: currentLabel })}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('theme.current', { theme: currentLabel })}
        onClick={() => setOpen((value) => !value)}
      >
        <CurrentIcon className="icon" aria-hidden="true" />
        <span className="theme-trigger-label">{currentLabel}</span>
        <ChevronDown className="theme-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="theme-popover" role="menu" aria-label={t('theme.label')}>
          <div className="theme-popover-title">{t('theme.label')}</div>
          {OPTIONS.map(({ value, icon: Icon }) => (
            <button
              key={value}
              type="button"
              className={`theme-menu-item${preference === value ? ' active' : ''}`}
              role="menuitemradio"
              aria-checked={preference === value}
              onClick={() => {
                setPreference(value);
                setOpen(false);
              }}
            >
              <Icon className="icon" aria-hidden="true" />
              <span>{t(`theme.${value}`)}</span>
              {preference === value && <Check className="theme-check" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
